/* eslint-disable no-redeclare, @typescript-eslint/no-redeclare */
import { ReactNode, useMemo, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  Anchor,
  AspectRatio,
  Autocomplete,
  Avatar,
  BackgroundImage,
  Badge,
  Blockquote,
  Box,
  Breadcrumbs,
  Burger,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  CloseButton,
  Code,
  Collapse,
  ColorInput,
  ColorPicker,
  ColorSwatch,
  CopyButton,
  createStyles,
  Dialog,
  Divider,
  Drawer,
  FileButton,
  FileInput,
  Flex,
  Grid,
  Group,
  Highlight,
  HoverCard,
  Image,
  Indicator,
  Input,
  InputBase,
  JsonInput,
  Kbd,
  List,
  Loader,
  LoadingOverlay,
  Mark,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  NavLink,
  Notification,
  NumberInput,
  Pagination,
  Paper,
  PasswordInput,
  Popover,
  Progress,
  Radio,
  RangeSlider,
  Rating,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Skeleton,
  Slider,
  Spoiler,
  Stack,
  Stepper,
  Switch,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Timeline,
  Title,
  Tooltip,
  TransferList,
  TransferListData,
  TypographyStylesProvider,
  UnstyledButton,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { Calendar, DatePicker, DateRangePicker, RangeCalendar, TimeInput, TimeRangeInput } from '@mantine/dates';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconAt,
  IconBell,
  IconCalendar,
  IconCheck,
  IconChevronDown,
  IconCode,
  IconDownload,
  IconHeart,
  IconInfoCircle,
  IconMail,
  IconMoonStars,
  IconPaperclip,
  IconPhoto,
  IconPlus,
  IconSearch,
  IconSettings,
  IconSun,
  IconTrash,
  IconUser,
} from '@tabler/icons';

const useStyles = createStyles((theme) => ({
  page: {
    minHeight: '100vh',
    color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[0],
  },
  header: {
    position: 'sticky',
    zIndex: 100,
    top: 0,
    borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2]}`,
    backgroundColor:
      theme.colorScheme === 'dark' ? theme.fn.rgba(theme.colors.dark[8], 0.96) : theme.fn.rgba(theme.white, 0.96),
    backdropFilter: 'blur(14px)',
  },
  headerInner: {
    maxWidth: 1440,
    margin: '0 auto',
    padding: `${theme.spacing.sm}px ${theme.spacing.xl}px`,
  },
  brandMark: {
    width: 38,
    height: 38,
    borderRadius: theme.radius.md,
    color: theme.white,
    background: theme.fn.gradient(theme.defaultGradient),
  },
  pageGrid: {
    display: 'grid',
    gridTemplateColumns: '220px minmax(0, 1fr)',
    gap: theme.spacing.xl * 2,
    maxWidth: 1440,
    margin: '0 auto',
    padding: `${theme.spacing.xl * 2}px ${theme.spacing.xl}px 96px`,

    [`@media (max-width: ${theme.breakpoints.md}px)`]: {
      gridTemplateColumns: '1fr',
      paddingTop: theme.spacing.xl,
    },
  },
  sidebar: {
    position: 'sticky',
    top: 92,
    alignSelf: 'start',

    [`@media (max-width: ${theme.breakpoints.md}px)`]: {
      display: 'none',
    },
  },
  sidebarLink: {
    display: 'block',
    padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
    borderRadius: theme.radius.sm,
    color: theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[7],
    fontSize: theme.fontSizes.sm,
    textDecoration: 'none',

    '&:hover': {
      color: theme.colorScheme === 'dark' ? theme.colors.blue[1] : theme.colors.blue[3],
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.blue[0],
    },
  },
  content: {
    minWidth: 0,
  },
  intro: {
    marginBottom: theme.spacing.xl * 2,
  },
  section: {
    scrollMarginTop: 100,
    marginBottom: 56,
  },
  sectionHeading: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing.lg,
    marginBottom: theme.spacing.lg,
  },
  demoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing.lg,

    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      gridTemplateColumns: '1fr',
    },
  },
  demo: {
    minWidth: 0,
    padding: theme.spacing.lg,
    border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2]}`,
    borderRadius: theme.radius.md,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
  },
  demoWide: {
    gridColumn: '1 / -1',
  },
  demoHeader: {
    minHeight: 43,
    marginBottom: theme.spacing.lg,
    paddingBottom: theme.spacing.sm,
    borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1]}`,
  },
  variantGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(116px, max-content))',
    gap: theme.spacing.md,
    alignItems: 'start',
  },
  variantItem: {
    display: 'flex',
    minWidth: 100,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing.xs,
  },
  variantLabel: {
    color: theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.gray[6],
    fontFamily: theme.fontFamilyMonospace,
    fontSize: 10,
    lineHeight: 1.25,
  },
  tokenGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: theme.spacing.sm,
  },
  token: {
    overflow: 'hidden',
    border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2]}`,
    borderRadius: theme.radius.sm,
  },
  colorStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(10, 1fr)',
    height: 38,
  },
  tokenMeta: {
    padding: theme.spacing.sm,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
  },
  metric: {
    padding: theme.spacing.md,
    border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2]}`,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
  },
  previewSurface: {
    position: 'relative',
    minHeight: 150,
    overflow: 'hidden',
    borderRadius: theme.radius.sm,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
  },
  checkerboard: {
    padding: theme.spacing.lg,
    backgroundImage:
      theme.colorScheme === 'dark'
        ? 'linear-gradient(45deg, #25262b 25%, transparent 25%), linear-gradient(-45deg, #25262b 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #25262b 75%), linear-gradient(-45deg, transparent 75%, #25262b 75%)'
        : 'linear-gradient(45deg, #f1f3f5 25%, transparent 25%), linear-gradient(-45deg, #f1f3f5 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f1f3f5 75%), linear-gradient(-45deg, transparent 75%, #f1f3f5 75%)',
    backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
    backgroundSize: '16px 16px',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  empty: {
    padding: 80,
    textAlign: 'center',
  },
}));

const sectionMeta = [
  { id: 'tokens', label: 'Theme tokens', keywords: 'colors typography radius spacing shadows palette' },
  { id: 'buttons', label: 'Buttons & actions', keywords: 'button action icon close burger file copy' },
  {
    id: 'inputs',
    label: 'Inputs & controls',
    keywords: 'input text select checkbox radio switch slider color rating chip',
  },
  { id: 'navigation', label: 'Navigation', keywords: 'tabs accordion pagination stepper navlink menu breadcrumbs' },
  {
    id: 'feedback',
    label: 'Feedback & status',
    keywords: 'alert notification loader progress skeleton overlay indicator',
  },
  { id: 'data', label: 'Data display', keywords: 'badge avatar card image table list timeline themeicon' },
  { id: 'overlays', label: 'Overlays', keywords: 'modal drawer dialog popover hovercard tooltip' },
  { id: 'typography', label: 'Typography', keywords: 'title text anchor code kbd blockquote highlight mark spoiler' },
  { id: 'layout', label: 'Layout & surfaces', keywords: 'paper grid flex group stack scroll aspect divider collapse' },
  { id: 'dates', label: 'Dates & time', keywords: 'calendar picker range date time' },
] as const;

const buttonVariants = ['filled', 'outline', 'light', 'white', 'default', 'subtle', 'gradient'] as const;
const actionIconVariants = ['subtle', 'filled', 'outline', 'light', 'default', 'transparent', 'gradient'] as const;
const badgeVariants = ['light', 'filled', 'outline', 'dot', 'gradient'] as const;
const themeIconVariants = ['filled', 'light', 'gradient', 'outline', 'default'] as const;
const inputVariants = ['default', 'filled', 'unstyled'] as const;
const alertVariants = ['filled', 'outline', 'light'] as const;
const accordionVariants = ['default', 'contained', 'filled', 'separated'] as const;
const tabVariants = ['default', 'outline', 'pills'] as const;
const sizes = ['xs', 'sm', 'md', 'lg', 'xl'] as const;
const gridExamples = [
  { id: 'first-wide', span: 4 },
  { id: 'second-wide', span: 8 },
  { id: 'first-quarter', span: 3 },
  { id: 'second-quarter', span: 3 },
  { id: 'third-quarter', span: 3 },
  { id: 'fourth-quarter', span: 3 },
] as const;

interface ShowcaseSectionProps {
  id: string;
  title: string;
  description: string;
  children: ReactNode;
}

const ShowcaseSection = ({ id, title, description, children }: ShowcaseSectionProps) => {
  const { classes } = useStyles();

  return (
    <section className={classes.section} id={id}>
      <div className={classes.sectionHeading}>
        <div>
          <Title order={2}>{title}</Title>
          <Text color="dimmed" mt={4}>
            {description}
          </Text>
        </div>
        <Badge variant="outline">section</Badge>
      </div>
      {children}
    </section>
  );
};

interface DemoProps {
  title: string;
  props?: string;
  wide?: boolean;
  children: ReactNode;
}

const Demo = ({ title, props, wide = false, children }: DemoProps) => {
  const { classes, cx } = useStyles();

  return (
    <div className={cx(classes.demo, { [classes.demoWide]: wide })}>
      <Group className={classes.demoHeader} position="apart" align="flex-start" noWrap>
        <Text weight={700}>{title}</Text>
        {props && (
          <Code color="gray" block={false}>
            {props}
          </Code>
        )}
      </Group>
      {children}
    </div>
  );
};

interface VariantItemProps {
  label: string;
  children: ReactNode;
}

const VariantItem = ({ label, children }: VariantItemProps) => {
  const { classes } = useStyles();

  return (
    <div className={classes.variantItem}>
      <Text className={classes.variantLabel}>{label}</Text>
      {children}
    </div>
  );
};

const MantineThemeShowcase = () => {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const [query, setQuery] = useState('');
  const [burgerOpened, setBurgerOpened] = useState(false);
  const [collapseOpened, setCollapseOpened] = useState(true);
  const [modalOpened, setModalOpened] = useState(false);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [dialogOpened, setDialogOpened] = useState(false);
  const [loadingVisible, setLoadingVisible] = useState(false);
  const [range, setRange] = useState<[number, number]>([24, 74]);
  const [calendarValue, setCalendarValue] = useState<Date | null>(new Date(2026, 7, 4));
  const [calendarRange, setCalendarRange] = useState<[Date | null, Date | null]>([
    new Date(2026, 7, 4),
    new Date(2026, 7, 9),
  ]);
  const [transferData, setTransferData] = useState<TransferListData>([
    [
      { value: 'react', label: 'React' },
      { value: 'mantine', label: 'Mantine' },
      { value: 'typescript', label: 'TypeScript' },
    ],
    [{ value: 'dayjs', label: 'Day.js' }],
  ]);

  const normalizedQuery = query.trim().toLowerCase();
  const visibleSections = useMemo(
    () =>
      sectionMeta.filter(
        ({ label, keywords }) =>
          normalizedQuery.length === 0 || `${label} ${keywords}`.toLowerCase().includes(normalizedQuery),
      ),
    [normalizedQuery],
  );
  const isVisible = (id: string) => visibleSections.some((section) => section.id === id);
  const primaryShade = typeof theme.primaryShade === 'number' ? theme.primaryShade : theme.primaryShade[colorScheme];
  const primaryColor = theme.colors[theme.primaryColor][primaryShade];
  const sampleDate = new Date(2026, 7, 4, 9, 30);

  return (
    <div className={classes.page}>
      <header className={classes.header}>
        <Group className={classes.headerInner} position="apart" noWrap>
          <Group noWrap>
            <Center className={classes.brandMark}>
              <IconCode size={21} />
            </Center>
            <div>
              <Text weight={800}>Mantine theme lab</Text>
              <Text size="xs" color="dimmed">
                Live output from MantineTheme.ts
              </Text>
            </div>
          </Group>
          <Group noWrap>
            <TextInput
              aria-label="Filter component sections"
              icon={<IconSearch size={16} />}
              placeholder="Filter components..."
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              sx={{ width: 230 }}
            />
            <Tooltip label={`Switch to ${colorScheme === 'dark' ? 'light' : 'dark'} mode`}>
              <ActionIcon
                aria-label={`Switch to ${colorScheme === 'dark' ? 'light' : 'dark'} mode`}
                size="lg"
                variant="default"
                onClick={() => toggleColorScheme()}
              >
                {colorScheme === 'dark' ? <IconSun size={19} /> : <IconMoonStars size={19} />}
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </header>

      <div className={classes.pageGrid}>
        <nav className={classes.sidebar} aria-label="Theme showcase sections">
          <Text size="xs" weight={700} color="dimmed" transform="uppercase" mb="xs">
            On this page
          </Text>
          {visibleSections.map(({ id, label }) => (
            <a className={classes.sidebarLink} href={`#${id}`} key={id}>
              {label}
            </a>
          ))}
        </nav>

        <main className={classes.content}>
          <div className={classes.intro}>
            <Badge variant="gradient" mb="sm">
              Mantine 5 · {colorScheme} theme
            </Badge>
            <Title order={1}>See the theme before changing it.</Title>
            <Text color="dimmed" size="lg" mt="sm" maw={780}>
              This page renders the application&apos;s active Mantine theme. Variants, sizes, common states, color
              tokens, overlays, and date controls are shown together so visual regressions are easy to spot.
            </Text>
            <Alert icon={<IconInfoCircle size={18} />} mt="xl" variant="light">
              Interactive examples are safe to click. Use the theme switch in the header to compare light and dark
              styles.
            </Alert>
          </div>

          {isVisible('tokens') && (
            <ShowcaseSection
              id="tokens"
              title="Theme tokens"
              description="Resolved values after Mantine merges its defaults with CustomMantineTheme."
            >
              <Stack spacing="lg">
                <div className={classes.tokenGrid}>
                  {Object.entries(theme.colors).map(([name, palette]) => (
                    <div className={classes.token} key={name}>
                      <div className={classes.colorStrip}>
                        {Object.entries(palette).map(([shade, color]) => (
                          <Tooltip label={`${name}.${shade} · ${color}`} key={`${name}-${shade}`}>
                            <div style={{ backgroundColor: color }} />
                          </Tooltip>
                        ))}
                      </div>
                      <Group className={classes.tokenMeta} position="apart" spacing="xs">
                        <Text size="sm" weight={700}>
                          {name}
                        </Text>
                        {name === theme.primaryColor && <Badge size="xs">primary</Badge>}
                      </Group>
                    </div>
                  ))}
                </div>

                <SimpleGrid cols={4} breakpoints={[{ maxWidth: 'sm', cols: 2 }]}>
                  <div className={classes.metric}>
                    <Text size="xs" color="dimmed">
                      primaryColor
                    </Text>
                    <Group spacing="xs" mt={5}>
                      <ColorSwatch color={primaryColor} size={20} />
                      <Code>{`${theme.primaryColor}.${primaryShade}`}</Code>
                    </Group>
                  </div>
                  <div className={classes.metric}>
                    <Text size="xs" color="dimmed">
                      defaultRadius
                    </Text>
                    <Code mt={5}>{String(theme.defaultRadius)}</Code>
                  </div>
                  <div className={classes.metric}>
                    <Text size="xs" color="dimmed">
                      fontFamily
                    </Text>
                    <Text mt={5} weight={700}>
                      {theme.fontFamily}
                    </Text>
                  </div>
                  <div className={classes.metric}>
                    <Text size="xs" color="dimmed">
                      lineHeight
                    </Text>
                    <Code mt={5}>{String(theme.lineHeight)}</Code>
                  </div>
                </SimpleGrid>

                <div className={classes.demoGrid}>
                  <Demo title="Font sizes" props="theme.fontSizes">
                    <Stack spacing="xs">
                      {sizes.map((size) => (
                        <Group position="apart" key={size}>
                          <Text size={size}>The quick brown fox · {size}</Text>
                          <Code>{theme.fontSizes[size]}px</Code>
                        </Group>
                      ))}
                    </Stack>
                  </Demo>
                  <Demo title="Radius & shadows" props="theme.radius / theme.shadows">
                    <Group align="flex-end">
                      {sizes.map((size) => (
                        <Paper key={size} radius={size} shadow={size} p="sm" withBorder>
                          <Text size="xs">{size}</Text>
                        </Paper>
                      ))}
                    </Group>
                  </Demo>
                </div>
              </Stack>
            </ShowcaseSection>
          )}

          {isVisible('buttons') && (
            <ShowcaseSection
              id="buttons"
              title="Buttons & actions"
              description="Every supported visual variant plus loading, disabled, compact, icon, and size states."
            >
              <div className={classes.demoGrid}>
                <Demo title="Button variants" props="variant" wide>
                  <div className={classes.variantGrid}>
                    {buttonVariants.map((variant) => (
                      <VariantItem label={variant} key={variant}>
                        <Button variant={variant}>Continue</Button>
                      </VariantItem>
                    ))}
                  </div>
                </Demo>
                <Demo title="Button sizes" props="size / compact">
                  <Stack align="flex-start">
                    {sizes.map((size) => (
                      <Button size={size} key={size}>
                        size=&quot;{size}&quot;
                      </Button>
                    ))}
                    <Button compact>Compact button</Button>
                  </Stack>
                </Demo>
                <Demo title="Button states" props="leftIcon / loading / disabled">
                  <Group>
                    <Button leftIcon={<IconPlus size={16} />}>Add item</Button>
                    <Button rightIcon={<IconArrowRight size={16} />} variant="outline">
                      Next
                    </Button>
                    <Button loading>Saving</Button>
                    <Button disabled>Disabled</Button>
                  </Group>
                </Demo>
                <Demo title="ActionIcon variants" props="variant" wide>
                  <div className={classes.variantGrid}>
                    {actionIconVariants.map((variant) => (
                      <VariantItem label={variant} key={variant}>
                        <ActionIcon aria-label={`${variant} action`} size="lg" variant={variant}>
                          <IconSettings size={19} />
                        </ActionIcon>
                      </VariantItem>
                    ))}
                  </div>
                </Demo>
                <Demo title="Native action helpers" props="Burger / CloseButton / UnstyledButton">
                  <Group>
                    <Burger
                      aria-label="Toggle navigation"
                      opened={burgerOpened}
                      onClick={() => setBurgerOpened((opened) => !opened)}
                    />
                    <CloseButton aria-label="Close" />
                    <UnstyledButton>
                      <Group spacing="xs">
                        <Avatar size="sm" radius="xl">
                          JB
                        </Avatar>
                        <Text size="sm" weight={600}>
                          Unstyled button
                        </Text>
                      </Group>
                    </UnstyledButton>
                  </Group>
                </Demo>
                <Demo title="FileButton & CopyButton" props="render props">
                  <Group>
                    <FileButton onChange={() => undefined} accept="image/png,image/jpeg">
                      {({ onClick: handleFileButtonClick }) => (
                        <Button leftIcon={<IconPhoto size={16} />} variant="light" onClick={handleFileButtonClick}>
                          Upload image
                        </Button>
                      )}
                    </FileButton>
                    <CopyButton value="Mantine theme lab">
                      {({ copied, copy }) => (
                        <Button color={copied ? 'green' : 'blue'} onClick={copy} variant="outline">
                          {copied ? 'Copied' : 'Copy text'}
                        </Button>
                      )}
                    </CopyButton>
                  </Group>
                </Demo>
              </div>
            </ShowcaseSection>
          )}

          {isVisible('inputs') && (
            <ShowcaseSection
              id="inputs"
              title="Inputs & controls"
              description="Input variants, validation states, selection controls, sliders, ratings, and color tools."
            >
              <div className={classes.demoGrid}>
                <Demo title="TextInput variants" props="variant">
                  <Stack>
                    {inputVariants.map((variant) => (
                      <TextInput
                        description="Helpful description"
                        icon={<IconAt size={16} />}
                        key={variant}
                        label={variant}
                        placeholder="hello@bookitup.de"
                        variant={variant}
                      />
                    ))}
                  </Stack>
                </Demo>
                <Demo title="Input states" props="required / error / disabled">
                  <Stack>
                    <TextInput label="Required" placeholder="Your name" required />
                    <TextInput defaultValue="wrong-value" error="This value is not valid" label="Error" />
                    <TextInput defaultValue="Read-only value" label="Read only" readOnly />
                    <TextInput disabled label="Disabled" placeholder="Unavailable" />
                  </Stack>
                </Demo>
                <Demo title="Input primitives" props="Input / InputBase">
                  <Stack>
                    <Input icon={<IconSearch size={16} />} placeholder="Bare Input component" />
                    <InputBase label="InputBase" placeholder="Custom input foundation" />
                    <Textarea label="Textarea" minRows={3} placeholder="Write a longer message..." />
                    <PasswordInput defaultValue="secret-password" label="PasswordInput" />
                  </Stack>
                </Demo>
                <Demo title="Numeric & structured" props="NumberInput / JsonInput">
                  <Stack>
                    <NumberInput defaultValue={42} label="Guests" max={100} min={0} />
                    <JsonInput
                      defaultValue={'{\n  "theme": "bookitup"\n}'}
                      formatOnBlur
                      label="Theme JSON"
                      minRows={4}
                      validationError="Invalid JSON"
                    />
                  </Stack>
                </Demo>
                <Demo title="Select family" props="Select / MultiSelect / Autocomplete" wide>
                  <SimpleGrid cols={3} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
                    <Select
                      data={['Wedding', 'Portrait', 'Corporate']}
                      label="Select"
                      placeholder="Event type"
                      searchable
                    />
                    <MultiSelect
                      data={['Photography', 'Video', 'Editing', 'Print']}
                      defaultValue={['Photography']}
                      label="MultiSelect"
                      placeholder="Services"
                    />
                    <Autocomplete
                      data={['Berlin', 'Hamburg', 'Munich', 'Cologne']}
                      label="Autocomplete"
                      placeholder="City"
                    />
                    <NativeSelect data={['Draft', 'Confirmed', 'Completed']} label="NativeSelect" />
                    <FileInput icon={<IconPaperclip size={16} />} label="FileInput" placeholder="Choose a file" />
                    <ColorInput defaultValue="#1D5191" format="hex" label="ColorInput" swatches={theme.colors.blue} />
                  </SimpleGrid>
                </Demo>
                <Demo title="Checkbox, Radio & Switch" props="checked / indeterminate / disabled" wide>
                  <SimpleGrid cols={3} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
                    <Stack spacing="sm">
                      <Checkbox label="Unchecked" />
                      <Checkbox defaultChecked label="Checked" />
                      <Checkbox indeterminate label="Indeterminate" />
                      <Checkbox disabled label="Disabled" />
                    </Stack>
                    <Radio.Group defaultValue="light" label="Radio group">
                      <Stack mt="xs" spacing="sm">
                        <Radio label="Light" value="light" />
                        <Radio label="Dark" value="dark" />
                        <Radio disabled label="Unavailable" value="system" />
                      </Stack>
                    </Radio.Group>
                    <Stack spacing="sm">
                      <Switch label="Off" />
                      <Switch defaultChecked label="On" />
                      <Switch defaultChecked label="With labels" offLabel="OFF" onLabel="ON" size="lg" />
                      <Switch disabled label="Disabled" />
                    </Stack>
                  </SimpleGrid>
                </Demo>
                <Demo title="Chip variants" props="variant / size">
                  <Stack>
                    <Group>
                      <Chip variant="filled">filled</Chip>
                      <Chip variant="outline">outline</Chip>
                    </Group>
                    <Chip.Group defaultValue={['mantine']} multiple>
                      <Group>
                        <Chip value="react">React</Chip>
                        <Chip value="mantine">Mantine</Chip>
                        <Chip value="typescript">TypeScript</Chip>
                      </Group>
                    </Chip.Group>
                  </Stack>
                </Demo>
                <Demo title="SegmentedControl & Rating" props="size / color">
                  <Stack align="flex-start">
                    <SegmentedControl data={['Day', 'Week', 'Month']} defaultValue="Week" />
                    <Rating defaultValue={3.5} fractions={2} />
                    <Rating
                      color="red"
                      defaultValue={4}
                      emptySymbol={<IconHeart size={24} />}
                      fullSymbol={<IconHeart size={24} fill="currentColor" />}
                    />
                  </Stack>
                </Demo>
                <Demo title="Slider & RangeSlider" props="marks / label / disabled" wide>
                  <Stack spacing={40} py="md">
                    <Slider
                      defaultValue={40}
                      marks={[
                        { value: 0, label: '0' },
                        { value: 50, label: '50' },
                        { value: 100, label: '100' },
                      ]}
                    />
                    <RangeSlider
                      marks={[
                        { value: 25, label: '25' },
                        { value: 75, label: '75' },
                      ]}
                      value={range}
                      onChange={setRange}
                    />
                    <Slider disabled defaultValue={55} />
                  </Stack>
                </Demo>
                <Demo title="ColorPicker & ColorSwatch" props="format / swatches" wide>
                  <Group align="flex-start" spacing="xl">
                    <ColorPicker
                      defaultValue="#1D5191"
                      format="hex"
                      swatches={[
                        theme.colors.blue[3],
                        theme.colors.red[3],
                        theme.colors.orange[3],
                        theme.colors.green[4],
                      ]}
                    />
                    <Stack>
                      <Group>
                        {['blue', 'red', 'orange', 'green', 'gray'].map((color) => (
                          <ColorSwatch color={theme.colors[color][3]} key={color} />
                        ))}
                      </Group>
                      <Text size="sm" color="dimmed">
                        Swatches use shade 3 from the active palettes.
                      </Text>
                    </Stack>
                  </Group>
                </Demo>
                <Demo title="TransferList" props="searchable two-list control" wide>
                  <TransferList
                    breakpoint="sm"
                    listHeight={150}
                    searchPlaceholder="Search..."
                    titles={['Available', 'Selected']}
                    value={transferData}
                    onChange={setTransferData}
                  />
                </Demo>
              </div>
            </ShowcaseSection>
          )}

          {isVisible('navigation') && (
            <ShowcaseSection
              id="navigation"
              title="Navigation"
              description="Navigation patterns shown with every component-level variant."
            >
              <div className={classes.demoGrid}>
                {tabVariants.map((variant) => (
                  <Demo title={`Tabs · ${variant}`} props={`variant="${variant}"`} key={variant}>
                    <Tabs defaultValue="overview" variant={variant}>
                      <Tabs.List>
                        <Tabs.Tab value="overview">Overview</Tabs.Tab>
                        <Tabs.Tab value="activity">Activity</Tabs.Tab>
                        <Tabs.Tab disabled value="archive">
                          Archive
                        </Tabs.Tab>
                      </Tabs.List>
                      <Tabs.Panel pt="md" value="overview">
                        <Text size="sm">Overview panel using the current theme.</Text>
                      </Tabs.Panel>
                      <Tabs.Panel pt="md" value="activity">
                        <Text size="sm">Activity panel.</Text>
                      </Tabs.Panel>
                    </Tabs>
                  </Demo>
                ))}
                <Demo title="Tabs · vertical" props="orientation" wide>
                  <Tabs defaultValue="profile" orientation="vertical">
                    <Tabs.List>
                      <Tabs.Tab icon={<IconUser size={15} />} value="profile">
                        Profile
                      </Tabs.Tab>
                      <Tabs.Tab icon={<IconSettings size={15} />} value="settings">
                        Settings
                      </Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel pl="md" value="profile">
                      Vertical tab content
                    </Tabs.Panel>
                    <Tabs.Panel pl="md" value="settings">
                      Settings content
                    </Tabs.Panel>
                  </Tabs>
                </Demo>
                {accordionVariants.map((variant) => (
                  <Demo title={`Accordion · ${variant}`} props={`variant="${variant}"`} key={variant}>
                    <Accordion defaultValue="first" variant={variant}>
                      <Accordion.Item value="first">
                        <Accordion.Control>First section</Accordion.Control>
                        <Accordion.Panel>Content styled by the active theme.</Accordion.Panel>
                      </Accordion.Item>
                      <Accordion.Item value="second">
                        <Accordion.Control>Second section</Accordion.Control>
                        <Accordion.Panel>Another panel.</Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>
                  </Demo>
                ))}
                <Demo title="Pagination" props="size / radius / disabled" wide>
                  <Stack align="flex-start">
                    <Pagination initialPage={3} total={10} withEdges />
                    <Pagination color="red" initialPage={2} radius="xl" size="sm" total={6} />
                    <Pagination disabled initialPage={2} total={5} />
                  </Stack>
                </Demo>
                <Demo title="Stepper" props="orientation / active" wide>
                  <Stepper active={1} breakpoint="sm">
                    <Stepper.Step description="Account details" label="Profile" />
                    <Stepper.Step description="Choose preferences" label="Settings" />
                    <Stepper.Step description="Review changes" label="Confirm" />
                  </Stepper>
                </Demo>
                <Demo title="NavLink variants" props="active variant">
                  <Stack spacing={2}>
                    {(['filled', 'light', 'subtle'] as const).map((variant) => (
                      <NavLink
                        active
                        description={`variant="${variant}"`}
                        icon={<IconCalendar size={18} />}
                        key={variant}
                        label="Calendar"
                        variant={variant}
                      />
                    ))}
                    <NavLink disabled icon={<IconTrash size={18} />} label="Disabled link" />
                  </Stack>
                </Demo>
                <Demo title="Menu & Breadcrumbs" props="trigger / items">
                  <Stack align="flex-start">
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <Button rightIcon={<IconChevronDown size={15} />} variant="default">
                          Open menu
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Label>Application</Menu.Label>
                        <Menu.Item icon={<IconSettings size={14} />}>Settings</Menu.Item>
                        <Menu.Item icon={<IconMail size={14} />}>Messages</Menu.Item>
                        <Menu.Divider />
                        <Menu.Item color="red" icon={<IconTrash size={14} />}>
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                    <Breadcrumbs>
                      <Anchor href="#navigation">Theme lab</Anchor>
                      <Anchor href="#navigation">Navigation</Anchor>
                      <Text>Current</Text>
                    </Breadcrumbs>
                  </Stack>
                </Demo>
              </div>
            </ShowcaseSection>
          )}

          {isVisible('feedback') && (
            <ShowcaseSection
              id="feedback"
              title="Feedback & status"
              description="Alerts, notifications, loading indicators, progress, and empty/loading states."
            >
              <div className={classes.demoGrid}>
                <Demo title="Alert variants" props="variant" wide>
                  <SimpleGrid cols={3} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
                    {alertVariants.map((variant) => (
                      <Alert icon={<IconInfoCircle size={18} />} key={variant} title={variant} variant={variant}>
                        This is a {variant} alert.
                      </Alert>
                    ))}
                  </SimpleGrid>
                </Demo>
                <Demo title="Alert colors" props="color">
                  <Stack>
                    <Alert color="blue" icon={<IconInfoCircle size={18} />} title="Information">
                      Helpful context for the user.
                    </Alert>
                    <Alert color="yellow" icon={<IconAlertTriangle size={18} />} title="Warning">
                      Check this before continuing.
                    </Alert>
                    <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Error">
                      Something needs attention.
                    </Alert>
                  </Stack>
                </Demo>
                <Demo title="Notification states" props="icon / loading">
                  <Stack>
                    <Notification icon={<IconCheck size={18} />} title="Saved" color="green">
                      Changes were saved successfully.
                    </Notification>
                    <Notification loading title="Uploading">
                      Your document is being uploaded.
                    </Notification>
                  </Stack>
                </Demo>
                <Demo title="Loader variants & sizes" props="variant / size" wide>
                  <Group spacing="xl">
                    {(['oval', 'bars', 'dots'] as const).map((variant) => (
                      <VariantItem label={variant} key={variant}>
                        <Loader variant={variant} />
                      </VariantItem>
                    ))}
                    {sizes.map((size) => (
                      <VariantItem label={`size=${size}`} key={size}>
                        <Loader size={size} />
                      </VariantItem>
                    ))}
                  </Group>
                </Demo>
                <Demo title="Progress variants" props="striped / animate / sections" wide>
                  <Stack>
                    <Progress value={54} />
                    <Progress animate color="orange" label="72%" size="xl" striped value={72} />
                    <Progress
                      sections={[
                        { value: 35, color: 'blue', label: '35%' },
                        { value: 25, color: 'orange', label: '25%' },
                        { value: 15, color: 'red', label: '15%' },
                      ]}
                      size="xl"
                    />
                  </Stack>
                </Demo>
                <Demo title="RingProgress" props="sections / label">
                  <Group>
                    <RingProgress
                      label={
                        <Text align="center" size="sm" weight={700}>
                          74%
                        </Text>
                      }
                      sections={[{ value: 74, color: 'blue' }]}
                    />
                    <RingProgress
                      roundCaps
                      sections={[
                        { value: 40, color: 'blue' },
                        { value: 25, color: 'orange' },
                        { value: 15, color: 'red' },
                      ]}
                    />
                  </Group>
                </Demo>
                <Demo title="Skeleton" props="circle / visible">
                  <Group noWrap>
                    <Skeleton circle height={52} />
                    <Stack spacing="xs" sx={{ flex: 1 }}>
                      <Skeleton height={10} radius="xl" />
                      <Skeleton height={10} radius="xl" width="70%" />
                      <Skeleton height={10} radius="xl" width="45%" />
                    </Stack>
                  </Group>
                </Demo>
                <Demo title="LoadingOverlay" props="visible / overlayBlur">
                  <div className={classes.previewSurface}>
                    <LoadingOverlay overlayBlur={2} visible={loadingVisible} />
                    <Center h={150}>
                      <Button onClick={() => setLoadingVisible((visible) => !visible)}>
                        {loadingVisible ? 'Hidden behind overlay' : 'Show overlay'}
                      </Button>
                    </Center>
                  </div>
                </Demo>
                <Demo title="Indicator" props="position / processing / inline">
                  <Group spacing="xl">
                    <Indicator inline label="3" size={16}>
                      <Avatar radius="xl">
                        <IconUser size={22} />
                      </Avatar>
                    </Indicator>
                    <Indicator color="green" inline processing size={12}>
                      <ActionIcon size="lg" variant="default">
                        <IconBell size={19} />
                      </ActionIcon>
                    </Indicator>
                    <Indicator color="orange" inline label="new" position="bottom-end" size={18}>
                      <Button variant="default">Updates</Button>
                    </Indicator>
                  </Group>
                </Demo>
              </div>
            </ShowcaseSection>
          )}

          {isVisible('data') && (
            <ShowcaseSection
              id="data"
              title="Data display"
              description="Compact status, identity, content, and tabular presentation components."
            >
              <div className={classes.demoGrid}>
                <Demo title="Badge variants" props="variant" wide>
                  <div className={classes.variantGrid}>
                    {badgeVariants.map((variant) => (
                      <VariantItem label={variant} key={variant}>
                        <Badge variant={variant}>{variant}</Badge>
                      </VariantItem>
                    ))}
                  </div>
                </Demo>
                <Demo title="Badge sizes & colors" props="size / color / fullWidth">
                  <Stack align="flex-start">
                    <Group>
                      {sizes.map((size) => (
                        <Badge key={size} size={size}>
                          {size}
                        </Badge>
                      ))}
                    </Group>
                    <Group>
                      {['blue', 'green', 'orange', 'red', 'gray'].map((color) => (
                        <Badge color={color} key={color}>
                          {color}
                        </Badge>
                      ))}
                    </Group>
                    <Badge fullWidth>Full width</Badge>
                  </Stack>
                </Demo>
                <Demo title="ThemeIcon variants" props="variant">
                  <div className={classes.variantGrid}>
                    {themeIconVariants.map((variant) => (
                      <VariantItem label={variant} key={variant}>
                        <ThemeIcon size="lg" variant={variant}>
                          <IconHeart size={19} />
                        </ThemeIcon>
                      </VariantItem>
                    ))}
                  </div>
                </Demo>
                <Demo title="Avatar" props="size / radius / group">
                  <Stack>
                    <Group align="flex-end">
                      {sizes.map((size) => (
                        <Avatar color="blue" key={size} radius="xl" size={size}>
                          {size.toUpperCase()}
                        </Avatar>
                      ))}
                    </Group>
                    <Avatar.Group spacing="sm">
                      <Avatar color="blue" radius="xl">
                        AB
                      </Avatar>
                      <Avatar color="orange" radius="xl">
                        CD
                      </Avatar>
                      <Avatar color="red" radius="xl">
                        EF
                      </Avatar>
                      <Avatar radius="xl">+5</Avatar>
                    </Avatar.Group>
                  </Stack>
                </Demo>
                <Demo title="Card & Image" props="shadow / withBorder / Card.Section">
                  <Card shadow="sm" p="lg" radius="md" withBorder>
                    <Card.Section>
                      <Image
                        alt="Abstract blue and orange gradient"
                        height={120}
                        src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='300'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' x2='1'%3E%3Cstop stop-color='%231D5191'/%3E%3Cstop offset='1' stop-color='%23FF5C5A'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='800' height='300' fill='url(%23g)'/%3E%3C/svg%3E"
                      />
                    </Card.Section>
                    <Group position="apart" mt="md">
                      <Text weight={700}>Styled card</Text>
                      <Badge>New</Badge>
                    </Group>
                    <Text color="dimmed" size="sm" mt="xs">
                      Card, image, badge and typography in one composition.
                    </Text>
                  </Card>
                </Demo>
                <Demo title="Image states" props="fit / radius / withPlaceholder">
                  <SimpleGrid cols={2}>
                    <Image
                      alt="Theme gradient"
                      caption="Loaded image"
                      height={110}
                      radius="md"
                      src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='220'%3E%3Crect width='400' height='220' fill='%2334629C'/%3E%3Ccircle cx='300' cy='20' r='180' fill='%23FDA632' fill-opacity='.8'/%3E%3C/svg%3E"
                    />
                    <Image caption="Placeholder" height={110} src={null} withPlaceholder />
                  </SimpleGrid>
                </Demo>
                <Demo title="Table" props="striped / highlightOnHover / borders" wide>
                  <div className={classes.tableWrap}>
                    <Table highlightOnHover striped withBorder withColumnBorders>
                      <thead>
                        <tr>
                          <th>Component</th>
                          <th>Category</th>
                          <th>Status</th>
                          <th>Coverage</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Button</td>
                          <td>Inputs</td>
                          <td>
                            <Badge color="green">Ready</Badge>
                          </td>
                          <td>7 variants</td>
                        </tr>
                        <tr>
                          <td>Alert</td>
                          <td>Feedback</td>
                          <td>
                            <Badge color="green">Ready</Badge>
                          </td>
                          <td>3 variants</td>
                        </tr>
                        <tr>
                          <td>Tabs</td>
                          <td>Navigation</td>
                          <td>
                            <Badge color="green">Ready</Badge>
                          </td>
                          <td>3 variants</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </Demo>
                <Demo title="List" props="ordered / icon / spacing">
                  <Group align="flex-start" spacing="xl">
                    <List
                      icon={
                        <ThemeIcon color="green" radius="xl" size={20}>
                          <IconCheck size={13} />
                        </ThemeIcon>
                      }
                      spacing="xs"
                    >
                      <List.Item>Colors resolved</List.Item>
                      <List.Item>Variants rendered</List.Item>
                      <List.Item>States compared</List.Item>
                    </List>
                    <List type="ordered" withPadding>
                      <List.Item>Inspect</List.Item>
                      <List.Item>Adjust</List.Item>
                      <List.Item>Verify</List.Item>
                    </List>
                  </Group>
                </Demo>
                <Demo title="Timeline" props="active / bullet / lineVariant">
                  <Timeline active={1} bulletSize={24} lineWidth={2}>
                    <Timeline.Item bullet={<IconCheck size={14} />} title="Theme loaded">
                      <Text color="dimmed" size="xs">
                        Custom overrides merged
                      </Text>
                    </Timeline.Item>
                    <Timeline.Item bullet={<IconCode size={14} />} title="Components rendered">
                      <Text color="dimmed" size="xs">
                        Reviewing active styles
                      </Text>
                    </Timeline.Item>
                    <Timeline.Item bullet={<IconDownload size={14} />} lineVariant="dashed" title="Ship changes">
                      <Text color="dimmed" size="xs">
                        Pending review
                      </Text>
                    </Timeline.Item>
                  </Timeline>
                </Demo>
              </div>
            </ShowcaseSection>
          )}

          {isVisible('overlays') && (
            <ShowcaseSection
              id="overlays"
              title="Overlays"
              description="Click the triggers to inspect portal-based surfaces against the active theme."
            >
              <div className={classes.demoGrid}>
                <Demo title="Modal, Drawer & Dialog" props="opened / overlay / shadow" wide>
                  <Group>
                    <Button onClick={() => setModalOpened(true)}>Open modal</Button>
                    <Button onClick={() => setDrawerOpened(true)} variant="outline">
                      Open drawer
                    </Button>
                    <Button onClick={() => setDialogOpened((opened) => !opened)} variant="light">
                      Toggle dialog
                    </Button>
                  </Group>
                  <Modal opened={modalOpened} onClose={() => setModalOpened(false)} title="Mantine modal">
                    <Text size="sm">This modal uses the current radius, colors, typography, and overlay styles.</Text>
                    <Group position="right" mt="lg">
                      <Button variant="default" onClick={() => setModalOpened(false)}>
                        Cancel
                      </Button>
                      <Button onClick={() => setModalOpened(false)}>Confirm</Button>
                    </Group>
                  </Modal>
                  <Drawer
                    opened={drawerOpened}
                    onClose={() => setDrawerOpened(false)}
                    padding="xl"
                    title="Mantine drawer"
                  >
                    <Text size="sm">Drawer content rendered in the portal.</Text>
                  </Drawer>
                  <Dialog
                    opened={dialogOpened}
                    withCloseButton
                    onClose={() => setDialogOpened(false)}
                    size="lg"
                    radius="md"
                  >
                    <Text size="sm" weight={700}>
                      Dialog preview
                    </Text>
                    <Text size="xs" color="dimmed">
                      A small non-modal surface fixed to the viewport.
                    </Text>
                  </Dialog>
                </Demo>
                <Demo title="Popover" props="position / withArrow / shadow">
                  <Popover position="bottom" shadow="md" withArrow>
                    <Popover.Target>
                      <Button variant="default">Toggle popover</Button>
                    </Popover.Target>
                    <Popover.Dropdown>
                      <Text size="sm">Popover content with an arrow.</Text>
                    </Popover.Dropdown>
                  </Popover>
                </Demo>
                <Demo title="HoverCard" props="openDelay / width / shadow">
                  <HoverCard shadow="md" width={280} openDelay={150}>
                    <HoverCard.Target>
                      <Anchor href="#overlays">Hover to preview a profile</Anchor>
                    </HoverCard.Target>
                    <HoverCard.Dropdown>
                      <Group noWrap>
                        <Avatar color="blue" radius="xl">
                          MB
                        </Avatar>
                        <div>
                          <Text size="sm" weight={700}>
                            Mantine Bot
                          </Text>
                          <Text size="xs" color="dimmed">
                            Theme inspection assistant
                          </Text>
                        </div>
                      </Group>
                    </HoverCard.Dropdown>
                  </HoverCard>
                </Demo>
                <Demo title="Tooltip positions" props="position / withArrow" wide>
                  <Group spacing="xl">
                    {(['top', 'right', 'bottom', 'left'] as const).map((position) => (
                      <Tooltip key={position} label={position} position={position} withArrow>
                        <Button variant="outline">{position}</Button>
                      </Tooltip>
                    ))}
                  </Group>
                </Demo>
              </div>
            </ShowcaseSection>
          )}

          {isVisible('typography') && (
            <ShowcaseSection
              id="typography"
              title="Typography"
              description="Heading hierarchy, text sizes and weights, inline semantics, and rich content defaults."
            >
              <div className={classes.demoGrid}>
                <Demo title="Title" props="order 1–6">
                  <Stack spacing="xs">
                    <Title order={1}>Heading 1</Title>
                    <Title order={2}>Heading 2</Title>
                    <Title order={3}>Heading 3</Title>
                    <Title order={4}>Heading 4</Title>
                    <Title order={5}>Heading 5</Title>
                    <Title order={6}>Heading 6</Title>
                  </Stack>
                </Demo>
                <Demo title="Text" props="size / weight / color / transform">
                  <Stack spacing="xs">
                    {sizes.map((size) => (
                      <Text key={size} size={size}>
                        Text size {size}
                      </Text>
                    ))}
                    <Text weight={700}>Bold text (700)</Text>
                    <Text color="dimmed">Dimmed text</Text>
                    <Text color="blue" transform="uppercase">
                      Uppercase primary text
                    </Text>
                  </Stack>
                </Demo>
                <Demo title="Anchor, Code & Kbd" props="inline elements">
                  <Stack align="flex-start">
                    <Anchor href="#typography">Default theme anchor</Anchor>
                    <Text>
                      Run <Code>npm run tsc</Code> after theme changes.
                    </Text>
                    <Text>
                      Press <Kbd>⌘</Kbd> + <Kbd>K</Kbd> to search.
                    </Text>
                    <Group spacing="xs">
                      <Code color="blue">blue</Code>
                      <Code color="orange">orange</Code>
                      <Code color="red">red</Code>
                    </Group>
                  </Stack>
                </Demo>
                <Demo title="Highlight & Mark" props="highlight / color">
                  <Stack>
                    <Highlight highlight="active theme">This sentence highlights the active theme values.</Highlight>
                    <Text>
                      Use <Mark color="orange">visual comparison</Mark> before changing tokens.
                    </Text>
                  </Stack>
                </Demo>
                <Demo title="Blockquote" props="cite / icon" wide>
                  <Blockquote cite="— Theme lab" icon={<IconInfoCircle size={24} />}>
                    A component theme is easiest to trust when every variant can be inspected in one place.
                  </Blockquote>
                </Demo>
                <Demo title="Spoiler" props="maxHeight / labels">
                  <Spoiler hideLabel="Show less" maxHeight={62} showLabel="Show more">
                    Mantine applies theme tokens through component styles. This longer paragraph demonstrates the
                    spoiler control and lets you inspect its link styling, text line height, transitions, and expanded
                    state. It is intentionally long enough to overflow the configured collapsed height.
                  </Spoiler>
                </Demo>
                <Demo title="TypographyStylesProvider" props="HTML content">
                  <TypographyStylesProvider>
                    <div>
                      <h3>Rich text heading</h3>
                      <p>
                        Paragraph styles from the typography provider, including <a href="#typography">links</a>.
                      </p>
                      <ul>
                        <li>Unordered list item</li>
                        <li>Second item</li>
                      </ul>
                    </div>
                  </TypographyStylesProvider>
                </Demo>
              </div>
            </ShowcaseSection>
          )}

          {isVisible('layout') && (
            <ShowcaseSection
              id="layout"
              title="Layout & surfaces"
              description="Spacing, borders, shadows, responsive grids, media, scroll, and disclosure primitives."
            >
              <div className={classes.demoGrid}>
                <Demo title="Paper radius & shadow" props="radius / shadow / withBorder" wide>
                  <SimpleGrid cols={5} breakpoints={[{ maxWidth: 'sm', cols: 2 }]}>
                    {sizes.map((size) => (
                      <Paper key={size} p="md" radius={size} shadow={size} withBorder>
                        <Text size="sm" weight={700}>
                          {size}
                        </Text>
                        <Text color="dimmed" size="xs">
                          radius + shadow
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Demo>
                <Demo title="Grid" props="Grid.Col span">
                  <Grid gutter="sm">
                    {gridExamples.map(({ id, span }) => (
                      <Grid.Col key={id} span={span}>
                        <Paper p="sm" withBorder>
                          <Text align="center" size="xs">
                            span {span}
                          </Text>
                        </Paper>
                      </Grid.Col>
                    ))}
                  </Grid>
                </Demo>
                <Demo title="SimpleGrid" props="cols / breakpoints">
                  <SimpleGrid cols={3}>
                    {[1, 2, 3].map((item) => (
                      <Paper p="lg" withBorder key={item}>
                        <Text align="center">{item}</Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Demo>
                <Demo title="Group, Stack, Flex & Center" props="spacing / position / gap" wide>
                  <SimpleGrid cols={4} breakpoints={[{ maxWidth: 'sm', cols: 2 }]}>
                    <Group position="center">
                      <Badge>Group</Badge>
                      <Badge>horizontal</Badge>
                    </Group>
                    <Stack align="center" spacing="xs">
                      <Badge>Stack</Badge>
                      <Badge>vertical</Badge>
                    </Stack>
                    <Flex gap="xs" justify="center" wrap="wrap">
                      <Badge>Flex</Badge>
                      <Badge>wrap</Badge>
                    </Flex>
                    <Center
                      h={70}
                      sx={(currentTheme) => ({
                        background:
                          currentTheme.colorScheme === 'dark'
                            ? currentTheme.colors.dark[6]
                            : currentTheme.colors.gray[1],
                      })}
                    >
                      <Badge>Center</Badge>
                    </Center>
                  </SimpleGrid>
                </Demo>
                <Demo title="AspectRatio & BackgroundImage" props="ratio / src">
                  <AspectRatio ratio={16 / 7}>
                    <BackgroundImage
                      radius="md"
                      src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='350'%3E%3Cdefs%3E%3ClinearGradient id='g'%3E%3Cstop stop-color='%230C203A'/%3E%3Cstop offset='1' stop-color='%231D5191'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='800' height='350' fill='url(%23g)'/%3E%3C/svg%3E"
                    >
                      <Center h="100%">
                        <Text color="white" weight={700}>
                          16 / 7 media surface
                        </Text>
                      </Center>
                    </BackgroundImage>
                  </AspectRatio>
                </Demo>
                <Demo title="Divider" props="orientation / variant / label">
                  <Stack>
                    <Divider label="Solid" labelPosition="center" />
                    <Divider label="Dashed" labelPosition="left" variant="dashed" />
                    <Divider label="Dotted" labelPosition="right" variant="dotted" />
                    <Group h={70} spacing="lg">
                      <Text>A</Text>
                      <Divider orientation="vertical" />
                      <Text>B</Text>
                    </Group>
                  </Stack>
                </Demo>
                <Demo title="Collapse" props="in / transitionDuration">
                  <Button mb="sm" onClick={() => setCollapseOpened((opened) => !opened)} variant="light">
                    Toggle collapse
                  </Button>
                  <Collapse in={collapseOpened}>
                    <Paper p="md" withBorder>
                      <Text size="sm">Animated collapsible content.</Text>
                    </Paper>
                  </Collapse>
                </Demo>
                <Demo title="ScrollArea" props="height / scrollbar">
                  <ScrollArea h={150} type="always" offsetScrollbars>
                    <Box w={620} p="sm">
                      {Array.from({ length: 8 }, (_, index) => (
                        <Paper key={index} mb="xs" p="xs" withBorder>
                          <Text size="sm">Scrollable row {index + 1} — deliberately wider than its container</Text>
                        </Paper>
                      ))}
                    </Box>
                  </ScrollArea>
                </Demo>
              </div>
            </ShowcaseSection>
          )}

          {isVisible('dates') && (
            <ShowcaseSection
              id="dates"
              title="Dates & time"
              description="Mantine Dates controls using the configured German locale and DD.MM.YYYY format."
            >
              <div className={classes.demoGrid}>
                <Demo title="DatePicker" props="inputFormat / locale">
                  <Stack>
                    <DatePicker defaultValue={sampleDate} label="Date" placeholder="Pick a date" />
                    <DatePicker clearable label="Clearable date" placeholder="DD.MM.YYYY" />
                    <DatePicker disabled label="Disabled date" value={sampleDate} />
                  </Stack>
                </Demo>
                <Demo title="DateRangePicker" props="amountOfMonths / range">
                  <DateRangePicker
                    defaultValue={[new Date(2026, 7, 4), new Date(2026, 7, 9)]}
                    label="Project range"
                    placeholder="Select range"
                  />
                </Demo>
                <Demo title="Calendar" props="value / onChange">
                  <Center>
                    <Calendar value={calendarValue} onChange={setCalendarValue} />
                  </Center>
                </Demo>
                <Demo title="RangeCalendar" props="range value">
                  <Center>
                    <RangeCalendar value={calendarRange} onChange={(value) => setCalendarRange(value)} />
                  </Center>
                </Demo>
                <Demo title="TimeInput" props="format / seconds">
                  <Stack>
                    <TimeInput defaultValue={sampleDate} format="24" label="Time" />
                    <TimeInput defaultValue={sampleDate} format="12" label="12-hour time" />
                    <TimeInput defaultValue={sampleDate} label="With seconds" withSeconds />
                  </Stack>
                </Demo>
                <Demo title="TimeRangeInput" props="range / format">
                  <TimeRangeInput
                    defaultValue={[sampleDate, new Date(2026, 7, 4, 17, 45)]}
                    format="24"
                    label="Working hours"
                  />
                </Demo>
              </div>
            </ShowcaseSection>
          )}

          {visibleSections.length === 0 && (
            <div className={classes.empty}>
              <ThemeIcon color="gray" radius="xl" size={54} variant="light">
                <IconSearch size={26} />
              </ThemeIcon>
              <Title order={3} mt="md">
                No matching section
              </Title>
              <Text color="dimmed" mt="xs">
                Try “button”, “input”, “navigation”, “feedback”, or “dates”.
              </Text>
              <Button mt="lg" variant="light" onClick={() => setQuery('')}>
                Clear filter
              </Button>
            </div>
          )}

          <Divider my="xl" />
          <Group position="apart">
            <Text color="dimmed" size="sm">
              Theme source: <Code>src/Theme/MantineTheme.ts</Code>
            </Text>
            <Anchor href="#tokens">
              Back to top <IconArrowRight size={13} style={{ transform: 'rotate(-90deg)', verticalAlign: 'middle' }} />
            </Anchor>
          </Group>
        </main>
      </div>
    </div>
  );
};

export default MantineThemeShowcase;
